export const SEO_SITE = {
  siteUrl: "https://www.rosemountfs-ifa.co.uk",
  siteName: "Rosemount Financial Solutions",
  author: "Rosemount Financial Solutions",
  logoPath: "/logo-blue.svg",
  linkedinUrl:
    "https://www.linkedin.com/company/rosemount-financial-solutions-ifa-ltd/",
};

export const getLogoUrl = () => `${SEO_SITE.siteUrl}${SEO_SITE.logoPath}`;

export const getPageUrl = (path = "") => {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${SEO_SITE.siteUrl}${cleanPath}`;
};

export const getOrganisationSchema = () => ({
  "@type": "Organization",
  "@id": `${SEO_SITE.siteUrl}/#organization`,
  name: SEO_SITE.siteName,
  url: SEO_SITE.siteUrl,
  logo: getLogoUrl(),
  image: getLogoUrl(),
  sameAs: [SEO_SITE.linkedinUrl],
  areaServed: "United Kingdom",
  description:
    "Rosemount Financial Solutions is an independent financial services network supporting mortgage advisers, protection specialists and financial planners.",
});

export const getWebsiteSchema = () => ({
  "@type": "WebSite",
  "@id": `${SEO_SITE.siteUrl}/#website`,
  name: SEO_SITE.siteName,
  url: SEO_SITE.siteUrl,
});

export const getWebPageSchema = ({ pageUrl, title, description }) => ({
  "@type": "WebPage",
  "@id": `${pageUrl}#webpage`,
  url: pageUrl,
  name: title,
  description,
  isPartOf: {
    "@id": `${SEO_SITE.siteUrl}/#website`,
  },
  about: {
    "@id": `${SEO_SITE.siteUrl}/#organization`,
  },
  primaryImageOfPage: {
    "@type": "ImageObject",
    url: getLogoUrl(),
  },
});

export const getServiceSchema = ({
  name = "Adviser Network Support",
  description,
  serviceType = [],
}) => ({
  "@type": "Service",
  name,
  provider: {
    "@id": `${SEO_SITE.siteUrl}/#organization`,
  },
  areaServed: "United Kingdom",
  serviceType,
  description,
});

export const getBreadcrumbSchema = (items = []) => ({
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
});

export const getFAQSchema = (faqs = []) => ({
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
});

export const getStructuredData = ({
  pageUrl,
  title,
  description,
  service,
  breadcrumbs = [],
  faqs = [],
  extraSchemas = [],
}) => {
  const graph = [
    getOrganisationSchema(),
    getWebsiteSchema(),
    getWebPageSchema({ pageUrl, title, description }),
  ];

  if (service) {
    graph.push(getServiceSchema(service));
  }

  if (breadcrumbs.length) {
    graph.push(getBreadcrumbSchema(breadcrumbs));
  }

  if (faqs.length) {
    graph.push(getFAQSchema(faqs));
  }

  if (extraSchemas.length) {
    graph.push(...extraSchemas);
  }

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  };
};

export const SEO_PAGES = {
  adviserHome: {
    path: "/Adviser",
    title: "Rosemount Adviser Network | Mortgage & Financial Adviser Support",
    description:
      "Join Rosemount Financial Solutions, an independent adviser network offering compliance, technology, training and business growth support for mortgage advisers and financial planners.",
    keywords:
      "Rosemount Financial Solutions, adviser network, mortgage adviser network, financial adviser network, IFA network, protection adviser support, mortgage broker support, financial planner support, compliance support, adviser technology, adviser business growth",
  },

  joinUs: {
    path: "/Adviser/JoinUs",
    title: "Join Rosemount | Adviser Network Benefits & Business Support",
    description:
      "Discover the benefits of joining Rosemount Financial Solutions, an independent adviser network offering compliance support, marketing support, paraplanning, training, technology and business development support for mortgage advisers, protection specialists and financial planners.",
    keywords:
      "join Rosemount, Rosemount adviser network, adviser network benefits, mortgage adviser network, financial adviser network, IFA network, appointed representative network, registered individual adviser, adviser compliance support, adviser marketing support, paraplanning support, financial adviser business development, mortgage broker support",
  },

  aboutUs: {
    path: "/Adviser/AboutUs",
    title: "About Rosemount | Adviser Network & Financial Services Support",
    description:
      "Learn about Rosemount Financial Solutions, an independent financial services network supporting mortgage advisers, protection specialists and financial planners with compliance, business development and adviser support.",
    keywords:
      "Rosemount Financial Solutions, adviser network, mortgage adviser network, financial adviser support, protection adviser network, financial planning network, compliance support, FCA adviser support, independent financial advisers",
  },

  events: {
    path: "/Adviser/Events",
    title:
      "Rosemount Adviser Events | Peer Groups, Masterclasses & Conferences",
    description:
      "Explore Rosemount adviser events, including peer groups, financial planning sessions, masterclasses, annual conferences and networking opportunities for mortgage advisers, protection specialists and financial planners.",
    keywords:
      "Rosemount events, adviser events, mortgage adviser events, financial adviser events, protection adviser events, financial planning events, adviser peer groups, adviser masterclasses, adviser annual conference, Rosemount Financial Solutions",
  },

  personalApproach: {
    path: "/Adviser/PersonalApproach",
    title: "Personal Adviser Support | Rosemount Financial Solutions",
    description:
      "Discover Rosemount's personal approach to adviser support, offering bespoke business development, compliance support, adviser events, training and a collaborative network for mortgage advisers and financial planners.",
    keywords:
      "personal adviser support, Rosemount Financial Solutions, adviser network, mortgage adviser support, financial adviser support, financial planner support, IFA network, adviser business development, adviser events, adviser peer groups, adviser masterclasses, compliance support, independent financial services network",
  },
  support: {
    path: "/Adviser/Support",
    title: "Adviser Support | Compliance, Training & Business Growth",
    description:
      "Discover how Rosemount Financial Solutions supports mortgage advisers, protection specialists and financial planners with compliance, training, business development and day-to-day adviser support.",
    keywords:
      "adviser support, mortgage adviser support, financial adviser support, protection adviser support, adviser compliance support, adviser training, adviser business growth, Rosemount Financial Solutions, IFA network, mortgage broker support, financial planning support",
  },
  technology: {
    path: "/Adviser/Technology",
    title: "Adviser Technology | CRM, Cashflow Modeller & ATR Profiler",
    description:
      "Discover the adviser technology available through Rosemount Financial Solutions, including CRM systems, mortgage sourcing tools, protection research, cashflow modelling and attitude to risk profiling.",
    keywords:
      "adviser technology, financial adviser CRM, mortgage sourcing technology, Twenty7tec, cashflow modeller, attitude to risk profiler, ATR profiler, adviser software, mortgage adviser technology, financial planning technology, protection research tools, Rosemount Financial Solutions",
  },
  contactUs: {
    path: "/ContactUs",
    title: "Contact Rosemount Financial Solutions | Adviser & Client Support",
    description:
      "Contact Rosemount Financial Solutions for adviser support, client queries, complaints information and general enquiries. Our friendly team are here to help.",
    keywords:
      "contact Rosemount Financial Solutions, Rosemount contact, adviser support contact, financial services contact, mortgage adviser support, financial adviser support, complaints procedure, Basingstoke financial services",
  },
  landingPage: {
    path: "/",
    title: "Rosemount Financial Solutions | Financial Advice & Adviser Network",
    description:
      "Rosemount Financial Solutions is an independent financial services firm and adviser network, supporting customers with financial advice and helping mortgage advisers, protection specialists and financial planners grow their businesses.",
    keywords:
      "Rosemount Financial Solutions, independent financial advice, financial adviser network, mortgage adviser network, financial planning, mortgage advice, protection advice, adviser support, financial services, IFA, Basingstoke financial advisers",
  },
  customerAboutUs: {
    path: "/AboutUs",
    title: "About Rosemount Financial Solutions | Independent Financial Advice",
    description:
      "Learn about Rosemount Financial Solutions, an independent financial planning firm with nationwide coverage, helping customers build bespoke plans for their financial goals.",
    keywords:
      "Rosemount Financial Solutions, about Rosemount, independent financial advice, financial planning firm, mortgage advice, protection advice, financial advisers, financial planning, IFA, Basingstoke financial advisers",
  },
  corporatePlanning: {
    path: "/OurServices/CorporatePlanning",
    title: "Corporate Planning | Business Protection & Director Pension Advice",
    description:
      "Explore corporate planning services from Rosemount Financial Solutions, including relevant life cover, key person insurance, shareholder protection, executive income protection and company pension planning.",
    keywords:
      "corporate planning, business protection, key person insurance, shareholder protection, relevant life cover, executive income protection, director pension contributions, company pension planning, business financial advice, Rosemount Financial Solutions",
  },
  equityRelease: {
    path: "/OurServices/EquityRelease",
    title: "Equity Release Advice | Unlock Property Wealth Safely",
    description:
      "Explore equity release advice from Rosemount Financial Solutions. Our qualified advisers can help homeowners aged 55 and over understand lifetime mortgages, property wealth, costs, risks and suitable alternatives.",
    keywords:
      "equity release advice, equity release, lifetime mortgage, release equity from home, property wealth, no negative equity guarantee, equity release adviser, retirement mortgage advice, Rosemount Financial Solutions",
  },
  insuranceProtection: {
    path: "/OurServices/InsuranceProtection",
    title:
      "Insurance & Protection Advice | Life Cover, Critical Illness & Income Protection",
    description:
      "Explore insurance and protection advice from Rosemount Financial Solutions, including life cover, critical illness cover, income protection and family financial protection tailored to your needs.",
    keywords:
      "insurance advice, protection advice, life cover, life insurance, critical illness cover, income protection, family protection, mortgage protection, financial protection advice, Rosemount Financial Solutions",
  },
  investmentPlanning: {
    path: "/OurServices/InvestmentPlanning",
    title: "Investment Planning Advice | Bespoke Investment Strategies",
    description:
      "Explore investment planning advice from Rosemount Financial Solutions. Our independent financial advisers can help you build a suitable investment strategy based on your goals, risk tolerance and timeframe.",
    keywords:
      "investment planning, investment advice, independent financial advice, investment strategy, financial planning, risk tolerance, regulated investment funds, responsible investing, sustainable investing, Rosemount Financial Solutions",
  },
  mortgagePlanning: {
    path: "/OurServices/MortgagePlanning",
    title:
      "Mortgage Planning Advice | First-Time Buyer, Remortgage & Home Moving",
    description:
      "Explore mortgage planning advice from Rosemount Financial Solutions. Our mortgage advisers can help first-time buyers, home movers, remortgage clients and those with complex mortgage needs find suitable options.",
    keywords:
      "mortgage planning, mortgage advice, first-time buyer mortgage, remortgage advice, home mover mortgage, mortgage adviser, mortgage broker, complex mortgage advice, buy to let mortgage, Rosemount Financial Solutions",
  },
  retirementPlanning: {
    path: "/OurServices/RetirementPlanning",
    title:
      "Retirement Planning Advice | Pension & Long-Term Financial Planning",
    description:
      "Explore retirement planning advice from Rosemount Financial Solutions. Our financial advisers can help you plan for retirement, review pensions, understand retirement options and build a long-term financial strategy.",
    keywords:
      "retirement planning, pension advice, retirement advice, financial planning, pension planning, personal pension advice, retirement options, pension transfer advice, long-term financial planning, Rosemount Financial Solutions",
  },
  ourServices: {
    path: "/OurServices",
    title: "Our Services | Mortgage, Pension, Investment & Protection Advice",
    description:
      "Explore Rosemount Financial Solutions services, including mortgage planning, retirement planning, investment planning, equity release, corporate planning and insurance/protection advice.",
    keywords:
      "financial services, mortgage planning, retirement planning, investment planning, equity release, corporate planning, insurance advice, protection advice, financial advice, Rosemount Financial Solutions",
  },
  rosemountBlog: {
    path: "/RosemountBlog",
    title:
      "Rosemount Blog | Financial Advice, Adviser News & Industry Insights",
    description:
      "Read the latest Rosemount Financial Solutions blog posts, including financial advice insights, adviser updates, mortgage news, protection planning, pensions, investments and industry commentary.",
    keywords:
      "Rosemount blog, financial advice blog, mortgage advice news, protection advice, pension planning, investment planning, financial adviser insights, adviser news, Rosemount Financial Solutions",
  },
  rosemountInMedia: {
    path: "/RosemountInMedia",
    title: "Rosemount in the Media | Financial Services News & Press Coverage",
    description:
      "Read Rosemount Financial Solutions media coverage, press features and financial services news, including mortgage, protection, adviser and industry updates.",
    keywords:
      "Rosemount in the media, Rosemount Financial Solutions news, financial services media coverage, mortgage adviser news, protection adviser news, IFA news, adviser network press coverage, mortgage industry news",
  },
  customerAboutUs: {
    path: "/AboutUs",
    title: "About Rosemount Financial Solutions | Independent Financial Advice",
    description:
      "Learn about Rosemount Financial Solutions, an independent financial planning firm with nationwide coverage, helping customers build bespoke financial plans for their goals.",
    keywords:
      "Rosemount Financial Solutions, about Rosemount, independent financial advice, financial planning firm, mortgage advice, protection advice, financial advisers, financial planning, IFA, Basingstoke financial advisers",
  },
};
